<template>
  <div
    class="size-full overflow-hidden"
    :class="{ 'absolute inset-0': cover, relative: !cover }"
    :style="!cover ? `width: ${width ? `${width}px` : '100%'}` : ''"
  >
    <img
      alt=""
      aria-hidden="true"
      decoding="async"
      fetchpriority="low"
      :src="img(source, { width: 50 }, fallbackImageSettings)"
      class="relative inset-0 size-full"
      :class="[
        noScale ? 'object-contain' : 'scale-125 object-cover object-top',
      ]"
      style="filter: blur(24px)"
      :loading="lazy ? 'lazy' : 'eager'"
      :width="Number(width)"
      :height="Number(height)"
    />
    <img
      :alt="alt"
      decoding="async"
      :src="sizes.src"
      :loading="lazy ? 'lazy' : 'eager'"
      :srcset="sizes.srcset"
      :sizes="sizes.sizes"
      class="absolute inset-0 size-full"
      :class="noScale ? 'object-contain' : 'object-cover object-top'"
      :width="Number(width)"
      :height="Number(height)"
      :fetchpriority="highPriority ? 'high' : 'auto'"
      @load="$emit('load')"
    />
  </div>
</template>

<script lang="ts">
import type { Link } from '@unhead/vue'
import {
  isSvg,
  fallbackImageSettings,
  defaultSizesOptimised,
  defaultFallbackSizes,
  notUpscaledDefaultSizes,
} from '~/composables/useSbImage'

export default defineComponent({
  name: 'HeroImage',
  props: {
    source: {
      type: String as PropType<string>,
      required: true,
    },
    optimiseDesktop: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    quality: {
      type: Number as PropType<number>,
      default: 70,
    },
    alt: {
      type: [String, undefined] as PropType<string | undefined>,
      default: undefined,
    },
    title: {
      type: [String, undefined] as PropType<string | undefined>,
      default: undefined,
    },
    noScale: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    cover: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    lazy: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
    highPriority: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    media: {
      type: [String, undefined] as PropType<
        'all' | 'desktop' | 'mobile' | undefined
      >,
      default: 'all',
    },
  },
  emits: ['load'],
  setup(props) {
    const route = useRoute()
    const img = useImage()
    const provider = 'storyblok'
    const mainImageModifiers = {
      format: 'webp',
      quality: props.quality,
    }

    const sizes = computed(() => {
      if (provider !== 'storyblok') {
        return null
      }
      const dimensions = props.source?.split('/')?.[5]?.split('x')
      if (!dimensions) {
        return null
      }
      const [imageWidth, imageHeight] = dimensions
      if (!imageWidth || !imageHeight) {
        return null
      }

      // fallback - storyblok only allows 4000x4000 images max
      if (parseInt(imageHeight, 10) > 4000 || parseInt(imageWidth, 10) > 4000) {
        return null
      }

      return [imageWidth, imageHeight]
    })

    const width = computed(() => {
      if (!sizes.value) {
        return null
      }
      return sizes.value[0]
    })

    const height = computed(() => {
      if (!sizes.value) {
        return null
      }
      return sizes.value[1]
    })

    const imageSizes = computed(() => {
      if (props.optimiseDesktop) {
        return defaultSizesOptimised
      }
      const fallbackWidth = width.value ? parseInt(width.value, 10) : 4000
      return notUpscaledDefaultSizes(fallbackWidth)
    })

    const variants = computed(() => {
      const opts = {
        sizes: imageSizes.value,
        provider,
        modifiers: {
          quality: mainImageModifiers.quality,
          width: parseSize(width.value),
          height: parseSize(height.value),
        },
      }
      const v = getVariants(img, props.source, opts)
      return v.map((i) => {
        i.src = img(
          props.source,
          { ...opts.modifiers, width: i.width, height: 950 },
          opts,
        )
        return i
      })
    })

    const _sizes = computed(() => {
      return getSizes(variants.value)
    })

    const isHomepage = computed(() => route.meta?.pageType === 'home')
    const shouldSkipHomepagePreload = computed(
      () => isHomepage.value && !props.highPriority,
    )

    const preload = computed(() => {
      if (
        shouldSkipHomepagePreload.value ||
        !props.source ||
        isSvg(props.source)
      ) {
        return []
      }
      let media: string
      if (!props.media || props.media === 'all') {
        media = 'all'
      } else if (props.media === 'desktop') {
        media = '(min-width: 640px)'
      } else {
        media = '(max-width: 639px)'
      }
      return [
        {
          hid: props.source,
          rel: 'preload',
          as: 'image',
          imagesrcset: _sizes.value.srcset,
          imagesizes: _sizes.value.sizes,
          media,
        },
      ]
    })

    useHead({ link: preload.value as Link[] })

    return {
      isSvg,
      provider,
      imageSizes,
      mainImageModifiers,
      defaultFallbackSizes,
      fallbackImageSettings,
      width,
      height,
      preload,
      sizes: _sizes,
      img,
    }
  },
})
</script>
